/**
 * Cosmetic-equip store — visual-only overlays that override the
 * gameplay equipment slot. Phase 16 ships cape + pet; mount is a bigger
 * piece (sit-on transform) and lands in a later phase.
 *
 * Persisted to localStorage AND fire-and-forgets to the server (Phase 35)
 * so the look survives across devices for authed users.
 */

import { create } from 'zustand';

const API_BASE = import.meta.env.DEV ? 'http://localhost:2570' : '/aiwari-api';

interface CosmeticStore {
  cape: string | null;   // cosmetic-shop item id (e.g. 'cape-tidal-storm')
  pet: string | null;    // 'pet-shadow-cat' etc.
  setCape: (id: string | null) => void;
  setPet: (id: string | null) => void;
  hydrate: (next: { cape: string | null; pet: string | null }) => void;
}

const KEY = 'aiwari-cosmetic-equip-v1';

function load(): { cape: string | null; pet: string | null } {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { cape: null, pet: null };
    const parsed = JSON.parse(raw) as { cape?: string | null; pet?: string | null };
    return { cape: parsed.cape ?? null, pet: parsed.pet ?? null };
  } catch { return { cape: null, pet: null }; }
}

function save(c: { cape: string | null; pet: string | null }) {
  try { localStorage.setItem(KEY, JSON.stringify(c)); } catch {}
}

function pushToServer(patch: { cape?: string | null; pet?: string | null }) {
  try {
    const stormId = localStorage.getItem('aiwari-storm-id') ?? '';
    if (!/^[0-9a-fA-F-]{36}$/.test(stormId)) return;
    const draftRaw = localStorage.getItem('aiwari-character-draft');
    if (!draftRaw) return;
    const draft = JSON.parse(draftRaw) as { name?: string };
    if (!draft.name) return;
    void fetch(`${API_BASE}/cosmetics/equip`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ stormId, name: draft.name, ...patch }),
    });
  } catch {}
}

export const useCosmetic = create<CosmeticStore>((set, get) => ({
  ...load(),
  setCape: (id) => {
    const next = { ...get(), cape: id };
    save(next);
    set({ cape: id });
    pushToServer({ cape: id });
  },
  setPet: (id) => {
    const next = { ...get(), pet: id };
    save(next);
    set({ pet: id });
    pushToServer({ pet: id });
  },
  hydrate: (next) => {
    save(next);
    set(next);
  },
}));
